import { computed, signal } from '@preact/signals-react';
import { AuthResponse, User, UserRole } from '../model/Types';
import { getProfilePicture } from '../api/TherapistApi';
import i18n from 'i18next';
import { refreshToken } from '../api/AuthApi';

let refreshInterval: NodeJS.Timeout | undefined = undefined;

// Helper funkcija za UTF-8 dekodovanje base64 stringova
function base64UrlDecode(str: string): string {
    // Zameni base64Url karaktere sa standard base64
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    
    // Dodaj padding ako je potrebno
    while (str.length % 4) {
        str += '=';
    }
    
    // Dekoduj base64 i konvertuj u UTF-8
    const binary = atob(str);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    
    // Dekoduj UTF-8 bytes u string
    return new TextDecoder('utf-8').decode(bytes);
}

const isExpired = (token: string | null): boolean => {
    if (token === null) {
        return true;
    }

    try {
        const payload = token.split('.')[1];
        const decoded = JSON.parse(base64UrlDecode(payload));
        const expirationTime = decoded['exp'] * 1000;

        return expirationTime < Date.now();
    } catch {
        return true;
    }
};

const initCurrentUser = () => {
    const token = localStorage.getItem('authToken');
    // Ako je token istekao, ne učitavamo korisnika
    if (token && isExpired(token)) {
        return null;
    }
    
    const user = localStorage.getItem('currentUser');
    if (user) {
        return JSON.parse(user);
    }
    return null;
};

const clearTimer = (timeout: NodeJS.Timeout | string | number | undefined) => {
    if (timeout) {
        clearInterval(timeout);
    }
};

export const logout = () => {
    currentUser.value = null;
    authConfig.value = null;
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
    localStorage.removeItem('appLanguage');
    clearTimer(refreshInterval);
    window.location.href = '/';
};

const setupRefreshTimer = () => {
    clearTimer(refreshInterval);

    refreshInterval = setInterval(async () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            clearTimer(refreshInterval);
            return;
        }
        const payload = token.split('.')[1];
        const decoded = JSON.parse(base64UrlDecode(payload));
        const expirationTime = decoded['exp'] * 1000;
        const timeLeft = expirationTime - Date.now();

        if (timeLeft < 70000) {
            refreshToken()
                .then((result) => {
                    const authResponse: AuthResponse = result.data;
                    localStorage.setItem('authToken', authResponse.access_token);
                    authConfig.value = authResponse.access_token;
                })
                .catch(() => {
                    clearTimer(refreshInterval);
                    logout();
                    alert(i18n.t('error.sessionExpired'));
                });
        }
    }, 60000);
};

const initAuthConfig = (): string | null => {
    const token = localStorage.getItem('authToken');

    if (token) {
        if (isExpired(token)) {
            // Tiho očisti stari, istekli token pri boot-u aplikacije — bez alerta
            // i bez forsiranog redirect-a. Ovaj slučaj se najčešće dešava kada
            // se korisnik vraća na stranicu nakon što mu je JWT već istekao van
            // aktivne sesije (npr. juče je bio prijavljen, zatvorio tab, danas
            // ponovo otvara). Alert "sesija je istekla" ima smisla samo kada
            // sesija istekne u toku aktivne upotrebe — to je pokriveno u
            // setupRefreshTimer .catch grani. Za protected rute, ProtectedRoute
            // će sam odraditi redirect na /unauthorized na osnovu isLoggedIn.
            localStorage.removeItem('currentUser');
            localStorage.removeItem('authToken');
            localStorage.removeItem('appLanguage');
            return null;
        }

        setupRefreshTimer();
        return token;
    }
    return null;
};

// Global state
export const currentUser = signal<User | null>(initCurrentUser());
export const authConfig = signal<string | null>(initAuthConfig());
export const isLoggedIn = computed(() => currentUser.value !== null);
export const isAdmin = computed(() => currentUser.value?.role === UserRole.ADMIN);

// Actions
export const login = async (username: string, token: string) => {
    authConfig.value = token;
    localStorage.setItem('authToken', token);

    setCurrentUser(username, token);
    setupRefreshTimer();
};

const setCurrentUser = (username: string, token: string) => {
    let user: User = createUserFromToken(token);
    currentUser.value = user;
    localStorage.setItem('currentUser', JSON.stringify(user));

    setProfilePicture(username, user);
};

const setProfilePicture = (username: string, user: User) => {
    if (user.role === UserRole.THERAPIST) {
        getProfilePicture(username)
            .then((result) => {
                let userToBeUpdated: User = { ...user };
                userToBeUpdated.avatar = result.data.imageData;
                currentUser.value = userToBeUpdated;
                localStorage.setItem('currentUser', JSON.stringify(userToBeUpdated));
            })
            .catch((error) => {
                console.log(error);
            });
    }
};

const createUserFromToken = (token: string): User => {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(base64UrlDecode(payload));

    return {
        firstName: decoded['given_name'],
        lastName: decoded['family_name'],
        name: decoded['name'],
        email: decoded['email'],
        role: createRolesFromDecodedToken(decoded),
        username: decoded['preferred_username'],
    };
};

const createRolesFromDecodedToken = (decoded: any): UserRole => {
    const possibleValues = Object.values(UserRole);
    const roles: string[] = decoded['realm_access']?.['roles'] || [];

    const role = roles.find((value) => possibleValues.includes(value as UserRole));

    if (!role) {
        // default value
        return UserRole.USER;
    }

    return role as UserRole;
};
